import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { type AuthState } from '@/common/types/auth.types';
import {
  type TeamApiResponse,
  type TeamResponse,
  type TeamFormPayload,
  type TeamListQuery,
  transformTeamResponse,
  transformTeamPayload,
} from '@/common/types/teams.types';

const baseURL = import.meta.env.VITE_API_EDMA_BASE_URL;

export const teamsApi = createApi({
  reducerPath: 'teamsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseURL}/api/teams`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    },
  }),
  tagTypes: ['Team', 'TeamList'],
  endpoints: (builder) => ({
    getTeams: builder.query<{ count: number; dataset: TeamResponse[] }, TeamListQuery | void>({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
        const { incidentId, search, groupId, ...restParams } = arg || {};
        const result = await baseQuery({
          url: '',
          params: {
            access_token: token,
            ...restParams,
            ...(search ? { 'filters[search]': search } : {}),
            ...(incidentId ? { 'filters[pvIncidentID]': incidentId } : {}),
            ...(groupId ? { 'filters[group_id]': groupId } : {}),
          },
        });
        if (result.error) {
          return { error: result.error };
        }
        // Transform parcels from string to array for each team
        const rawData = result.data as { count: number; dataset: TeamApiResponse[] };
        return {
          data: {
            count: rawData.count,
            dataset: rawData.dataset.map(transformTeamResponse),
          },
        };
      },
      providesTags: ['TeamList'],
    }),
    getTeam: builder.query<TeamResponse, string>({
      queryFn: async (teamId, { getState }, _extraOptions, baseQuery) => {
        const token = (getState() as AuthState).auth.tokenVerification?.accessToken;
        const result = await baseQuery({
          url: `/${teamId}`,
          params: { access_token: token },
        });
        if (result.error) {
          return { error: result.error };
        }
        // Transform parcels from string to array
        const rawData = result.data as TeamApiResponse;
        return { data: transformTeamResponse(rawData) };
      },
      providesTags: (_result, _error, id) => [{ type: 'Team', id }],
    }),
    saveTeam: builder.mutation<TeamResponse, TeamFormPayload>({
      query: (formPayload) => {
        const body = transformTeamPayload(formPayload);
        return {
          url: formPayload?.id ? `/${formPayload.id}` : '',
          method: formPayload?.id ? 'PUT' : 'POST',
          body,
        };
      },
      invalidatesTags: (_result, _error, formPayload) => [
        'TeamList',
        ...(formPayload.id ? [{ type: 'Team' as const, id: formPayload.id }] : []),
      ],
    }),
    deleteTeam: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamList'],
    }),
  }),
});

export const { useGetTeamsQuery, useGetTeamQuery, useSaveTeamMutation, useDeleteTeamMutation } =
  teamsApi;
